/**
 * Cableado de accesibilidad del elemento editable de Froala. Vive en su propio módulo
 * (y no dentro de TextEditor.js) para poder probarse sin arrastrar Froala, que no
 * funciona en jsdom y está doblado globalmente en los tests.
 */

/**
 * Escribe (o retira) un atributo del elemento editable de Froala.
 * @param {HTMLElement} element - Elemento `.fr-element`
 * @param {string} name - Nombre del atributo
 * @param {string|undefined} value - Valor, o nullish/vacío para retirarlo
 * @returns {void}
 */
function setOrRemoveAttribute(element, name, value) {
  if (!value) {
    element.removeAttribute(name);
    return;
  }
  element.setAttribute(name, String(value));
}

/**
 * Aplica sobre el elemento editable de Froala el rol y los atributos ARIA que el
 * componente recibe. Se exporta para poder probarla sin montar Froala, que no funciona
 * en jsdom.
 * @param {HTMLElement} element - Elemento `.fr-element`
 * @param {Object} attributes - Atributos a aplicar
 * @param {string} [attributes.id] - Id que FormField clona en su hijo
 * @param {string} [attributes.ariaLabel] - Nombre accesible literal
 * @param {string} [attributes.ariaLabelledBy] - Id del elemento que da el nombre
 * @param {string} [attributes.ariaDescribedBy] - Ids de la ayuda y los errores
 * @param {boolean} [attributes.ariaInvalid] - Si el campo está en error
 * @param {boolean} [attributes.ariaRequired] - Si el campo es obligatorio
 * @returns {void}
 */
export function applyEditorAccessibilityAttributes(element, { id, ariaLabel, ariaLabelledBy, ariaDescribedBy, ariaInvalid, ariaRequired } = {}) {
  if (!element) return;

  // Sin `role` el rol computado del contenteditable es `generic`, donde el nombre
  // accesible está prohibido (axe: aria-prohibited-attr) y los lectores de pantalla
  // no lo exponen de forma fiable.
  element.setAttribute('role', 'textbox');
  element.setAttribute('aria-multiline', 'true');

  setOrRemoveAttribute(element, 'id', id);
  // `<label for>` no alcanza a un contenteditable: el nombre lo da aria-labelledby
  // (preferido, apunta al label real) o, en su defecto, aria-label.
  setOrRemoveAttribute(element, 'aria-labelledby', ariaLabelledBy);
  setOrRemoveAttribute(element, 'aria-label', ariaLabelledBy ? undefined : ariaLabel);
  setOrRemoveAttribute(element, 'aria-describedby', ariaDescribedBy);
  setOrRemoveAttribute(element, 'aria-invalid', ariaInvalid ? 'true' : undefined);
  setOrRemoveAttribute(element, 'aria-required', ariaRequired ? 'true' : undefined);
}
